import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import useApi from "../../../../../utils/api";
import { getErrorMessage } from "../../../../../utils/helpers/errors";

export const useWorkSummaryHistory = () => {
	const { t } = useTranslation();
	const { authGet } = useApi();
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState();

	const getWorkSummary = useCallback(
		async (date, user, controller) => {
			setLoading(true);

			try {
				const response = await authGet("/shift/work-summary", {
					signal: controller?.signal,
					params: {
						date,
						user,
					},
				});

				if (response) {
					setData(response);
				}
			} catch (err) {
				getErrorMessage(err, t);
			} finally {
				setLoading(false);
			}
		},
		[authGet, setLoading, setData, t],
	);

	return { loading, data, getWorkSummary };
};

export const useHistory = () => {
	const { t } = useTranslation();
	const { authGet } = useApi();
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);

	const getHistory = useCallback(
		async (date, user, controller) => {
			setLoading(true);
			try {
				const response = await authGet("/time-sheet/history", {
					signal: controller?.signal,
					params: {
						date,
						user,
					},
				});

				if (response) {
					setData(response);
				}
			} catch (err) {
				getErrorMessage(err, t);
			} finally {
				setLoading(false);
			}
		},
		[setLoading, setData, authGet, t],
	);

	return { loading, data, getHistory };
};

export const useSchedule = () => {
	const { t } = useTranslation();
	const { authGet } = useApi();
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);

	const getEvents = useCallback(async (endpoint, date, controller) => {
		setLoading(true);
		try {
			const response = await authGet(endpoint, {
				params: {
					startDate: date,
					endDate: date
				},
				signal: controller?.signal
			});
			if (response) {
				setData(response);
			}
		} catch (err) {
			getErrorMessage(err, t);
		} finally {
			setLoading(false);
		}
	}, [setLoading, setData, authGet, t]);

	return { data, loading, getEvents };
}

export const useClockExceptions = () => {
	const { t } = useTranslation();
	const { authGet, authPut } = useApi();
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);

	const getExceptions = useCallback(async (endpoint, params, controller) => {
		setLoading(true);
		try {
			const response = await authGet(endpoint, {
				params,
				signal: controller?.signal
			});
			if (response) {
				setData(response);
			}
		} catch (err) {
			getErrorMessage(err, t);
		} finally {
			setLoading(false);
		}
	}, [authGet, t]);

	const updateStatus = useCallback(async (url, data, id, onSuccess) => {
		try {
			const response = await authPut(url, { data });
			if (response) {
				setData((prev) => {
					return prev?.map((exception) => exception.id === id ? response : exception);
				});
				onSuccess?.(response);
			}
		} catch (err) {
			getErrorMessage(err, t);
		}
	}, [authPut, setData, t]);

	return { data, loading, getExceptions, updateStatus, setData };
}

export const useAdjustments = () => {
	const { t } = useTranslation();
	const { authGet } = useApi();
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);

	const getAdjustments = useCallback(async (user, params, controller) => {
		setLoading(true);
		try {
			const response = await authGet(`/retro-adjustment/user/${user.id}`, {
				params,
				signal: controller?.signal
			});
			if (response) {
				setData(response);
			}
		} catch (err) {
			getErrorMessage(err, t);
		} finally {
			setLoading(false);
		}
	}, [authGet, t]);

	return { data, loading, getAdjustments };
}
